// The invoice list (UI kit W3): a table with search, status tabs, sorting
// and pages. The tab and search live in the address (?filter=overdue&q=),
// so a filtered list can be bookmarked, shared or reloaded.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { compareStamps, formatDate, today } from '@/core/dates';
import { filterMatches, INVOICE_FILTERS, initialsOf, type Invoice, type InvoiceFilter, invoiceMatchesQuery, isDraft, statusOn, totalsOf } from '@/core/invoice';
import { track } from '@/data/analytics';
import { deleteInvoice, saveInvoice } from '@/data/store';
import { clientNameLabel, filterLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { Avatar, ButtonLink, EmptyState, PageHead, PageLoading, StatusChip } from '@/ui/controls';
import { ConfirmDialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';
import { Menu } from '@/ui/Menu';
import { useToast } from '@/ui/Toasts';

const PAGE_SIZE = 20;
type SortKey = 'issued' | 'due' | 'amount';

export const invoiceHref = (invoice: Invoice) => (isDraft(invoice) ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`);

export function InvoicesPage() {
  const { invoices } = useAppData();
  const [params, setParams] = useSearchParams();
  const filter = (INVOICE_FILTERS as readonly string[]).includes(params.get('filter') ?? '') ? (params.get('filter') as InvoiceFilter) : 'all';
  const query = params.get('q') ?? '';
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'issued', descending: true });
  const [page, setPage] = useState(0);
  usePageTitle(t('invoicesInvoices'));
  const day = today();

  const statuses = useMemo(() => new Map((invoices ?? []).map((i) => [i.id, statusOn(i, day)])), [invoices, day]);
  const counts = useMemo(() => Object.fromEntries(INVOICE_FILTERS.map((f) => [f, (invoices ?? []).filter((i) => filterMatches(f, statuses.get(i.id)!)).length])), [invoices, statuses]);
  const visible = useMemo(() => {
    const list = (invoices ?? []).filter((i) => filterMatches(filter, statuses.get(i.id)!) && invoiceMatchesQuery(i, query));
    const by = {
      issued: (a: Invoice, b: Invoice) => compareStamps(a.issueDate, b.issueDate) || compareStamps(a.createdAt, b.createdAt),
      due: (a: Invoice, b: Invoice) => compareStamps(a.dueDate, b.dueDate),
      amount: (a: Invoice, b: Invoice) => totalsOf(a).total.minor - totalsOf(b).total.minor,
    }[sort.key];
    return list.sort((a, b) => (sort.descending ? -by(a, b) : by(a, b)));
  }, [invoices, statuses, filter, query, sort]);

  if (!invoices) return <PageLoading />;

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const shown = visible.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const waiting = invoices.filter((i) => ['pending', 'partiallyPaid', 'overdue'].includes(statuses.get(i.id)!));
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(0);
  };

  return (
    <div className="page">
      <PageHead
        title={t('invoicesInvoices')}
        subtitle={invoices.length ? t('webInvoicesSubtitle', { count: invoices.length, waiting: waiting.length }) : undefined}
        actions={
          <>
            {invoices.length > 0 && (
              <label className="search" style={{ width: 320 }}>
                <Icon name="search" size={18} />
                <span className="vh">{t('invoicesSearchClientNumberOrAmount')}</span>
                <input type="search" value={query} placeholder={t('invoicesSearchClientNumberOrAmount')} onChange={(e) => {
                    if (!query.trim() && e.target.value.trim()) track('search_used', { screen: 'invoices' });
                    setParam('q', e.target.value || null);
                  }} />
              </label>
            )}
          </>
        }
      />
      {invoices.length === 0 ? (
        <div className="card">
          <EmptyState
            title={t('invoicesNoInvoicesYet')}
            body={t('invoicesInvoicesYouCreateShowUp')}
            action={
              <ButtonLink to="/invoices/new" variant="action" icon="plus">
                {t('invoicesCreateAnInvoice')}
              </ButtonLink>
            }
          />
        </div>
      ) : (
        <>
          <div className="tabs" role="group" aria-label={t('invoicesStatus')}>
            {INVOICE_FILTERS.map((f) => (
              <button key={f} type="button" className="tab" aria-pressed={f === filter} onClick={() => {
                if (f !== filter) track('invoices_filtered', { filter: f });
                setParam('filter', f === 'all' ? null : f);
              }}>
                {filterLabel(f)}
                <span className="count">{counts[f]}</span>
              </button>
            ))}
          </div>
          {visible.length === 0 ? (
            <div className="card">
              <EmptyState badge="search" title={query ? t('invoicesNoMatch', { query }) : t('invoicesNoneInFilter', { filter })} />
            </div>
          ) : (
            <>
              <InvoiceTable invoices={shown} sort={sort} onSort={(key) => setSort((s) => ({ key, descending: s.key === key ? !s.descending : true }))} />
              {pages > 1 && (
                <div className="between">
                  <span className="caption">{t('webShowingRange', { from: current * PAGE_SIZE + 1, to: current * PAGE_SIZE + shown.length, total: visible.length })}</span>
                  <nav className="pager" aria-label={t('webPages')}>
                    <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>
                      {t('webPrevious')}
                    </button>
                    {Array.from({ length: pages }, (_, i) => (
                      <button key={i} type="button" aria-current={i === current ? 'page' : undefined} onClick={() => setPage(i)}>
                        {i + 1}
                      </button>
                    ))}
                    <button type="button" disabled={current === pages - 1} onClick={() => setPage(current + 1)}>
                      {t('webNext')}
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Invoices as table rows; a row opens the invoice (a draft in the editor). */
export function InvoiceTable({ invoices, compact, sort, onSort, hideClient }: { invoices: Invoice[]; compact?: boolean; sort?: { key: SortKey; descending: boolean }; onSort?: (key: SortKey) => void; hideClient?: boolean }) {
  const navigate = useNavigate();
  const { unsynced, user } = useAppData();
  const [deleting, setDeleting] = useState<Invoice | null>(null);
  const toast = useToast();
  const day = today();

  const header = (key: SortKey, label: string, end?: boolean) => {
    if (!onSort) return <th className={end ? 'end' : undefined}>{label}</th>;
    const active = sort?.key === key;
    return (
      <th className={end ? 'end' : undefined} aria-sort={active ? (sort!.descending ? 'descending' : 'ascending') : 'none'}>
        <button type="button" className="th-sort" onClick={() => onSort(key)}>
          {label}
          {active && <Icon name="down" size={14} style={{ transform: sort!.descending ? undefined : 'rotate(180deg)' }} />}
        </button>
      </th>
    );
  };

  const remove = (invoice: Invoice) => {
    if (!user) return;
    deleteInvoice(user.uid, invoice.id);
    track('invoice_deleted', { was_draft: isDraft(invoice), from: 'list' });
    toast(isDraft(invoice) ? t('invoicesDraftDeleted', { number: invoice.number }) : t('invoicesInvoiceDeleted', { number: invoice.number }), {
      action: {
        label: t('commonUndo'),
        run: () => {
          saveInvoice(user.uid, invoice);
          track('invoice_restored');
        },
      },
    });
  };

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>{compact ? t('invoicesClient') : t('invoicesInvoice')}</th>
            {!hideClient && !compact && <th>{t('invoicesClient')}</th>}
            {!compact && header('issued', t('invoicesIssued'))}
            {!compact && header('due', t('invoicesDue'))}
            {header('amount', t('invoicesAmount'), true)}
            <th className="end">{t('invoicesStatus')}</th>
            {!compact && (
              <th>
                <span className="vh">{t('detailMore')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const status = statusOn(invoice, day);
            const href = invoiceHref(invoice);
            return (
              <tr
                key={invoice.id}
                data-href={href}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a, button, [role="menu"]')) return;
                  if (window.getSelection()?.toString()) return;
                  navigate(href);
                }}
              >
                {compact ? (
                  <td>
                    <span className="row" style={{ gap: 10 }}>
                      <Avatar initials={initialsOf(invoice.client.name)} seed={invoice.client.name} />
                      <span className="stack-1" style={{ minWidth: 0 }}>
                        <Link to={href} className="row-link">
                          {clientNameLabel(invoice.client)}
                        </Link>
                        <span className="caption">
                          {invoice.number} · {t('pdfDueOn', { dueDate: formatDate(invoice.dueDate) })}
                        </span>
                      </span>
                    </span>
                  </td>
                ) : (
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link to={href} className="row-link">
                    {invoice.number}
                  </Link>
                  {unsynced.has(invoice.id) && (
                    <span className="caption" style={{ display: 'block' }}>
                      {t('invoiceNotSynced')}
                    </span>
                  )}
                </td>
                )}
                {!hideClient && !compact && (
                  <td>
                    <span className="row" style={{ gap: 10 }}>
                      <Avatar initials={initialsOf(invoice.client.name)} seed={invoice.client.name} />
                      <span className={invoice.client.name.trim() ? undefined : 'muted'}>{clientNameLabel(invoice.client)}</span>
                    </span>
                  </td>
                )}
                {!compact && <td className="muted nowrap">{formatDate(invoice.issueDate)}</td>}
                {!compact && <td className="muted nowrap">{formatDate(invoice.dueDate)}</td>}
                <td className="end amount">{totalsOf(invoice).total.format()}</td>
                <td className="end">
                  <StatusChip status={status} />
                </td>
                {!compact && (
                  <td className="end" style={{ width: 60 }}>
                    <Menu
                      fixed
                      trigger={(props) => (
                        <button type="button" className="iconbtn iconbtn--sm" aria-label={t('webActionsFor', { number: invoice.number })} {...props}>
                          <Icon name="more" size={18} />
                        </button>
                      )}
                    >
                      {(close) => (
                        <>
                          <Link role="menuitem" className="menu__item" to={href} onClick={close}>
                            <Icon name="file" size={18} />
                            {t('invoicesOpen')}
                          </Link>
                          <Link role="menuitem" className="menu__item" to={`/invoices/${invoice.id}/edit`} onClick={close}>
                            <Icon name="pen" size={18} />
                            {isDraft(invoice) ? t('invoicesEditDraft') : t('invoicesEditInvoice')}
                          </Link>
                          <div className="menu__sep" />
                          <button
                            role="menuitem"
                            type="button"
                            className="menu__item menu__item--danger"
                            onClick={() => {
                              close();
                              setDeleting(invoice);
                            }}
                          >
                            <Icon name="trash" size={18} />
                            {isDraft(invoice) ? t('invoicesDeleteDraft') : t('invoicesDeleteInvoice')}
                          </button>
                        </>
                      )}
                    </Menu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {deleting && (
        <ConfirmDialog
          open
          danger
          title={isDraft(deleting) ? t('editorDeleteThisDraft') : t('detailDeleteThisInvoice')}
          message={t('detailDeleteMessage', { number: deleting.number })}
          confirmLabel={t('commonDelete')}
          onConfirm={() => remove(deleting)}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
